import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import styles from './horizontal-chart.module.scss';
import HorizontalChartUnified from './horizontal-chart-unified';
import { RankingSkeleton, Shimmer } from '@flexo/atoms';
import { useDispatch, useSelector } from 'react-redux';
import { IconWrapper } from '@flexo/general';
import { FetchEnergyRankingUnifiedAsync, FetchEconomicsRankingUnifiedAsync } from '@flexo/reducers';
import { useLocation, useParams } from 'react-router-dom';
import { formatToMaxFourDigits, getSavedLang } from '@flexo/general';
import { navigateToMember, navigateToSite } from '@flexo/resolvers';
import { RIGHT_DRAWER } from '@flexo/reducers';

const ENV = (import.meta as any).env;

const savedLang = getSavedLang() || null;
const browserLanguage = savedLang?.split('_')[1] || navigator.language.split('-')[0] || ENV.VITE_APP_LANGUAGE;

const org = ENV?.VITE_APP_ORG || 'base';

export default function HorizontalWrapper(props: any) {

  const { t } = useTranslation();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  const { id } = useParams();
  const ENTITY_ID = props?.directId || id;
  const ThemeStore = useSelector((state: any) => state.theme);
  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);
  const calendar = useSelector((state: any) => state.calendar);
  const rightDrawer = useSelector((state: any) => state.rightDrawer);
  const dispatch = useDispatch();
  const chartWrapperRef = React.useRef<HTMLDivElement>(null);
  const { hiveCalendar } = calendar || {};
  const { selectedDate, selectedTimeSet } = (hiveCalendar || {}) as any;
  
  const scope = pathSegment?.includes('member') ? 'member' : 'community';



  let sitesListLenght =
    metadata?.entities?.sites?.filter((site) => site?.communityId === ENTITY_ID)
      ?.length || 0;
  let kpiContext = 'Energy';
  if (pathSegment?.includes('economics')) {
    kpiContext = 'Economics';
  }
  let rawData = kpi?.communityRanking || null;
  let LOADING_STATE = kpi?.[`${kpiContext}KpiDataCommunityRankingAsyncStatus`];

  if (pathSegment?.includes('member')) {
    sitesListLenght =
      metadata?.entities?.sites?.filter((site) => site?.memberId === ENTITY_ID)
        ?.length || 0;
    rawData = kpi?.memberRanking || null;
    LOADING_STATE = kpi?.[`${kpiContext}KpiDataMemberRankingAsyncStatus`];
  }

  const totalSites = sitesListLenght || 0;




  const isDouble = Array.isArray(props.graphKeys?.[0]);
  const [activeKeys, setActiveKeys] = useState<any>(
    isDouble
      ? [props.graphKeys[0] || [], props.graphKeys[1] || []]
      : props.graphKeys || []
  );

  const [data, setData] = useState<any>([]);
  const [maxValue, setMaxValue] = useState<any>(0);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [rendered, setRendered] = useState(false);
  const [shouldHide, setShouldHide] = useState<any>(sitesListLenght < 2);
  const fullRankingRequestedRef = useRef(false);

  function handleNavigation(line: any) {
    if (pathSegment?.includes('member')) {
      navigateToMember(
        props?.context === 'energy'
          ? 'member-energy'
          : props?.context === 'economics'
          ? 'member-economics'
          : 'member',
        line?.memberId
      );
    } else {
      navigateToSite(
        props?.context === 'energy'
          ? 'site-energy'
          : props?.context === 'economics'
          ? 'site-economics'
          : 'site',
        line?.siteId
      );
    }
  }

  const getItemsList = () => {
    const sitesList = (metadata?.entities as any)?.site || [];
    const dedupeBySiteId = (list: any[]) => {
      const seen = new Set<string>();
      return (list || []).filter((item: any) => {
        const siteId = Array.isArray(item) ? item?.[0]?.siteId : item?.siteId;
        if (!siteId) return true;
        if (seen.has(siteId)) return false;
        seen.add(siteId);
        return true;
      });
    };
    const maybeTop5 = (list: any[]) => (props?.isDrawerChart ? list : (list || []).slice(0, 5));

    if (props?.context === 'energy') {
      const consumptionRanking =
        rawData?.energyRanking?.[0]?.rankings?.find(


          (ranking: any) => ranking?.kpi === props?.kpiNameLabels?.[0]
        ) || null;

      const productionRanking =
        rawData?.energyRanking?.[0]?.rankings?.find(
          (ranking: any) => ranking?.kpi === props?.kpiNameLabels?.[1]
        ) || null;


      if (isDouble) {
        if (consumptionRanking?.site) {
          let sites = consumptionRanking?.site
            .map((_site: any) => ({
              ..._site,
              ...sitesList.find(
                (entitiesSsite: any) => entitiesSsite.siteId === _site.siteId
              ),
            }))
            .sort((a, b) => (a?.ranking < b?.ranking ? -1 : 1));

          sites = sites.map((site: any) => ({
            ...site,
            kpi: [
              site.kpi,
              
                productionRanking?.site ? 
              productionRanking?.site?.find(
                (prodSite: any) => prodSite?.siteId === site?.siteId
              )?.kpi || [] : []
            
            ],
          }));

          const deduped = dedupeBySiteId(sites);
          return maybeTop5(deduped);
        }
      } else {
        if (consumptionRanking?.site) {
          const sites = consumptionRanking?.site
            .map((_site: any) => ({
              ..._site,
              ...sitesList.find(
                (entitiesSsite: any) => entitiesSsite.siteId === _site.siteId
              ),
            }))
            .sort((a, b) => (a?.ranking > b?.ranking ? 1 : -1));
          const paired = sites.map((site: any) => [
            site,
            consumptionRanking?.site.find(
              (_site: any) => _site.siteId === site?.siteId
            ),
          ]);
          const deduped = dedupeBySiteId(paired);
          return maybeTop5(deduped);
        }
      }


    } else if (props?.context === 'economics') {

      
      const economicsRanking =
        rawData?.economicsRanking?.[0]?.rankings?.[0] || null;
      const list = economicsRanking?.site
        .map((_site: any) => ({
          ..._site,
          ...sitesList.find(
            (entitiesSsite: any) => entitiesSsite.siteId === _site.siteId
          ),
        }))
        .sort((a, b) => (a?.ranking > b?.ranking ? 1 : -1));
      const deduped = dedupeBySiteId(list || []);
      return maybeTop5(deduped);
    }
    return [];
  };

  function getKpiValue(item: any, index: number | null = 0) {
    if (isDouble) {
      let kpi;
      if (index !== null) {
        kpi = item?.kpi[index]?.find(
          (datum: any) => props?.kpiNameLabels?.[index] === datum.name
        );
      } else {
        kpi = item?.kpi?.find((datum: any) => props?.rankingKpi === datum.name);
      }
      const value = formatToMaxFourDigits(kpi?.value?.value || kpi?.value || '-_-', props?.context === 'economics');
      const unit = kpi?.unit || '';
      return props?.context === 'economics'
        ? `${unit} ${value}`
        : `${value} ${unit}`;
    } else {
      const kpi = item?.kpi?.find(
        (datum: any) => props?.rankingKpi === datum.name
      );
      const value = formatToMaxFourDigits(kpi?.value?.value || kpi?.value || 0, props?.context === 'economics');
      const unit = kpi?.unit || '';
      return props?.context === 'economics'
        ? `${unit} ${value}`
        : `${value} ${unit}`;
    }
  }

  const getEntityName = () => {
    if (pathSegment?.includes('member')) {
      const member = metadata?.entities?.member?.find((member: any) => member?.memberId === ENTITY_ID);
      return [member?.memberFirstName, member?.memberLastName].filter(Boolean).join(' ');
    } else {
      return metadata?.entities?.community?.find((community: any) => community?.communityId === ENTITY_ID)?.communityName || '';
    }
  }

  const openDrawer = () => {
    console.log('[HorizontalWrapper] openDrawer called', { fullRankingRequested: fullRankingRequestedRef.current, titleCallback: props?.titleCallback, totalSites, ENTITY_ID });

    const configs = {
      ...props,
      directId: ENTITY_ID,
      isDrawerChart: true,
      title: `widgets.horizontalChart.${props?.context}.allSites`,
      pageTitle: t(`widgets.horizontalChart.${props?.context}.drawerPageTitle`, { entity_name: getEntityName() }),
      pageSubtitle: t(`widgets.horizontalChart.drawerpageSubtitle`),
    }

    delete configs.titleCallback;
    
    dispatch(RIGHT_DRAWER.SetDrawer({
      drawerConfig: configs,
      type: 'HORIZONTAL_CHART',
    } as any));
    if (!fullRankingRequestedRef.current && props?.titleCallback === 'HORIZONTAL_CHART') {
      console.log('[HorizontalWrapper] Fetching ranking with fetchAll: true');
      fullRankingRequestedRef.current = true;
      // Token is auto-injected by httpService interceptor (Clerk auth)
      const commonPayload: any = {
        org,
        scope,
        entityId: ENTITY_ID,
        totalSites,
        selectedDate,
        selectedTimeSet,
        fetchAll: true,
      };
      if (props?.context === 'economics') {
        dispatch((FetchEconomicsRankingUnifiedAsync as any)(commonPayload));
      } else {
        dispatch((FetchEnergyRankingUnifiedAsync as any)(commonPayload));
      }
    }
  }

  const handleButtonClick = (key: string) => {
    if (isDouble && typeof activeKeys[0] !== 'string') {
      const index = props.buttons.indexOf(key);
      // FLEXO-6054: guard against an unknown button (indexOf → -1) or a partial
      // graphKeys where the second group is absent — `graphKeys[1][index]` on an
      // undefined group threw "Cannot read properties of undefined (reading '1')".
      const firstKey = props.graphKeys?.[0]?.[index];
      const secondKey = props.graphKeys?.[1]?.[index];
      if (index < 0 || firstKey === undefined || secondKey === undefined) {
        return;
      }
      if (
        (activeKeys[0] as any).length === 1 &&
        (activeKeys[0] as any).includes(firstKey)
      ) {
        return;
      }
      if (
        (activeKeys[0] as any).length === props?.combinedKeys?.[0]?.length &&
        (activeKeys[0] as any)?.includes(firstKey) &&
        props?.combinedKeys?.[0]?.includes(firstKey)
      ) {
        return;
      }
      const newActiveKeys: any = [[], []];
      if (props.combinedKeys?.[0]?.includes(firstKey)) {
        const firstCombinedKeyGroup = props.combinedKeys[0];
        const secondCombinedKeyGroup = props.combinedKeys[1];
        const firstAreAllKeysActive = firstCombinedKeyGroup.every((k) =>
          (activeKeys[0] as any).includes(k)
        );
        const secondAreAllKeysActive = secondCombinedKeyGroup.every((k) =>
          (activeKeys[1] as any).includes(k)
        );
        newActiveKeys[0] = (
          firstAreAllKeysActive
            ? (activeKeys[0] as any).filter(
                (k) => !firstCombinedKeyGroup.includes(k)
              )
            : [
                ...(activeKeys[0] as any),
                ...firstCombinedKeyGroup.filter(
                  (k) => !(activeKeys[0] as any).includes(k)
                ),
              ]
        ).filter((x) => x);
        newActiveKeys[1] = (
          secondAreAllKeysActive
            ? (activeKeys[1] as any).filter(
                (k) => !secondCombinedKeyGroup.includes(k)
              )
            : [
                ...(activeKeys[1] as any),
                ...secondCombinedKeyGroup.filter(
                  (k) => !(activeKeys[1] as any).includes(k)
                ),
              ]
        ).filter((x) => x);
      } else if (
        (activeKeys[0] as any).includes(firstKey) &&
        activeKeys[1].includes(secondKey)
      ) {
        (activeKeys[0] as any).forEach((k: any, i: number) => {
          if (k !== firstKey) {
            if (k !== firstKey) {
              newActiveKeys[0].push(k);
              newActiveKeys[1].push(activeKeys[1][i]);
            }
          }
        });
      } else if (
        !(activeKeys[0] as any).includes(firstKey) &&
        !activeKeys[1].includes(secondKey)
      ) {
        newActiveKeys[0] = [...(activeKeys[0] as any), firstKey];
        newActiveKeys[1] = [...(activeKeys[1] as any), secondKey];
      }
      setActiveKeys(newActiveKeys);
    } else {
      if (activeKeys.length === 1 && activeKeys.includes(key)) {
        return;
      }
      if (
        activeKeys.length === props?.combinedKeys?.length &&
        activeKeys?.includes(key) &&
        props?.combinedKeys?.includes(key)
      ) {
        return;
      }
      if (props.combinedKeys?.includes(key)) {
        const combinedKeyGroup = props.combinedKeys;
        const areAllKeysActive = combinedKeyGroup.every((k) =>
          activeKeys.includes(k)
        );
        setActiveKeys((prev: any) =>
          (areAllKeysActive
            ? prev.filter((k: any) => !combinedKeyGroup.includes(k))
            : [
                ...prev,
                ...combinedKeyGroup.filter((k: any) => !prev.includes(k)),
              ]
          ).filter((x: any) => x)
        );
      } else {
        setActiveKeys((prev: any) =>
          (prev.includes(key)
            ? prev.filter((k: any) => k !== key)
            : [...prev, key]
          ).filter((x: any) => x)
        );
      }
    }
  };

  useEffect(() => {
    if (!rawData) {
      setData([]);
      return;
    }
    const _data = getItemsList() || [];
    setData(_data);
    if (isDouble) {
      setMaxValue(
        Math.max(
          ...(_data || [])
            .map((site: any) =>
              site.kpi
                .map((kpiI: any) =>
                  kpiI
                    .filter((datum: any) =>
                      props?.kpiNameLabels?.flat()?.includes(datum.name)
                    )
                    .map((x: any) => x.value?.value || x.value)
                )
                .flat()
            )
            .flat()
        ) || 0
      );
    } else {
      setMaxValue(
        Math.max(
          ...(_data || [])
            .map((site: any) => site?.kpi?.flat())
            .flat()
            .map((kpi: any) => kpi?.value?.value || kpi?.value || 0)
        ) || 0
      );
    }
  }, [rawData]);

  useEffect(() => {
    setShouldHide(sitesListLenght < 2);
  }, [sitesListLenght]);

  // Reset fullRankingRequested when date/period changes so the drawer can fetch fresh data
  useEffect(() => {
    fullRankingRequestedRef.current = false;
  }, [selectedDate, selectedTimeSet]);

  if (shouldHide === true) {
    return null;
  }



  return (
    <div
      id="HorizontalChart__Wrapper"
      className={styles.HorizontalChart__Wrapper}
      ref={chartWrapperRef}
    >
      {LOADING_STATE === 'PENDING' && (
        <div
          style={{
            width: 'calc(100% - 30px)',
            height: 'calc(100% - 30px)',
            position: 'absolute',
            top: 20,
            left: 20,
          }}
        >
          <Shimmer />
        </div>
      )}
      {(LOADING_STATE === 'PENDING' && data?.length === 0) ||
      LOADING_STATE == 'ERROR' ? (
        <RankingSkeleton title={props.title} context={props?.context} />
      ) : data
          ?.map((datum: any) => datum.ranking)
          .every((rank: any) => rank === null) ? (
        <>
          <div 
          className={styles.HorizontalChart__Title}
          >{t(props.title)}</div>
          <div
            className={styles.HorizontalChart__Buttons__Wrapper}
            style={{ marginBottom: 10 }}
          >
            {props.buttons.map((button: any, index: number) => (
              <div
                key={`HorizontalChart__${index}__button__${button}`}
                className={`
                    ${styles.HorizontalChart__Button}
                    ${
                      props?.combinedKeys?.[0]?.includes(
                        props.graphKeys?.[0]?.[index]
                      )
                        ? styles.HorizontalChart__Button__Touch
                        : ''
                    }
                  `}
              >
                {t(button)}
                <div
                  className={styles.HorizontalChart__Button__Underline}
                  style={{
                    backgroundColor: `${
                      ThemeStore?.colors[props.colors[index].color]
                    }${props.colors[index].colorShade}`,
                  }}
                ></div>
              </div>
            ))}
          </div>
          <RankingSkeleton
            title={props.title}
            context={props?.context}
            hideTitle={true}
          />
        </>
      ) : (
        <>
          <div 
            className={ props?.titleCallback === 'HORIZONTAL_CHART' ? styles.HorizontalChart__Title__Callback : styles.HorizontalChart__Title}
            onClick={ props?.titleCallback === 'HORIZONTAL_CHART' ? () => openDrawer() : () => null}
          >
            {t(props.title)}
            { props?.titleCallback === 'HORIZONTAL_CHART' && <IconWrapper iconName="chevron-right" /> }
          </div>
          <div className={styles.HorizontalChart__Buttons__Wrapper}>
            {props.buttons.map((button: any, index: number) => (
              <div
                key={`HorizontalChart__${index}__button__${button}`}
                className={`
                    ${styles.HorizontalChart__Button}
                    ${
                      props?.combinedKeys?.[0]?.includes(
                        props.graphKeys?.[0]?.[index]
                      )
                        ? styles.HorizontalChart__Button__Touch
                        : ''
                    }
                    ${
                      !activeKeys[0]?.includes(props.graphKeys?.[0]?.[index])
                        ? styles.HorizontalChart__Button__Disabled
                        : ''
                    }
                  `}
                onClick={() =>
                  handleButtonClick(
                    typeof props.graphKeys?.[0] === 'string'
                      ? props.graphKeys[index]
                      : button
                  )
                }
              >
                {t(button)}
                <div
                  className={styles.HorizontalChart__Button__Underline}
                  style={{
                    backgroundColor: `${
                      ThemeStore?.colors[props.colors[index].color]
                    }${props.colors[index].colorShade}`,
                  }}
                ></div>
              </div>
            ))}
          </div>
          <div ref={chartWrapperRef}>
            {data.length === 0 ? (
              <></>
            ) : props?.context === 'energy' ? (
              (data || []).map(
                (line: any, lineIndex: number, lineArray: any) => (
                  <div
                    key={`energy_horzontal_chart_${lineIndex}`}
                    className={styles.HorizontalChart__Wrapper__Line}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      className={styles.HorizontalChart__Wrapper__Line__Label}
                      onClick={() => handleNavigation(line)}
                    >
                      <span>{line?.siteName}</span>
                      <span className="font-flexo-light">
                        &nbsp; {line?.pod}
                      </span>
                    </div>
                    {line?.siteType?.[0]?.siteType === 'producer' ||
                    line?.siteType?.type === 'producer' ? (
                      <></>
                    ) : (
                      <div
                        className={styles.HorizontalChart__Wrapper__Line__Rank}
                      >
                        <div
                          className={
                            styles.HorizontalChart__Wrapper__Line__Rank__Value__Name__Wrapper
                          }
                        >
                          <p
                            className={
                              styles.HorizontalChart__Wrapper__Line__Rank__Value
                            }
                          >
                            {getKpiValue(line, 0)}
                          </p>
                          <p
                            className={
                              styles.HorizontalChart__Wrapper__Line__Rank__Name
                            }
                          >
                            {' '}
                            {t(props?.rankingKpiName?.[0])}
                          </p>
                        </div>
                        <HorizontalChartUnified
                          data={{
                            kpi: line?.kpi?.[0],
                            ...props,
                            graphKeys: props?.graphKeys?.[0],
                            rankingKpiName: props?.rankingKpiName?.[0],
                            activeKeys: activeKeys[0],
                            combinedKeysIndex: 0,
                            maxValue: (maxValue !== null && maxValue > 0) ? maxValue : 0,
                            activeTooltip,
                            setActiveTooltip: (data: any) =>
                              setActiveTooltip(data),
                            key: `energy_horzontal_chart_${lineIndex}_0`,
                            tooltipColors: props?.tooltipColors?.[0],
                            ...(lineIndex === lineArray.length - 1 &&
                            (line?.siteType?.[0]?.siteType === 'consumer' ||
                              line?.siteType?.type === 'consumer')
                              ? { setRendered }
                              : {}),
                          }}
                        />
                      </div>
                    )}
                    {line?.siteType?.[0]?.siteType === 'consumer' ||
                    line?.siteType?.type === 'consumer' ? (
                      <></>
                    ) : (
                      <div
                        className={styles.HorizontalChart__Wrapper__Line__Rank}
                        style={{ marginTop: 8 }}
                      >
                        <div
                          className={
                            styles.HorizontalChart__Wrapper__Line__Rank__Value__Name__Wrapper
                          }
                        >
                          <p
                            className={
                              styles.HorizontalChart__Wrapper__Line__Rank__Value
                            }
                          >
                            {getKpiValue(line, 1)}
                          </p>
                          <p
                            className={
                              styles.HorizontalChart__Wrapper__Line__Rank__Name
                            }
                          >
                            {' '}
                            {t(props?.rankingKpiName?.[1])}
                          </p>
                        </div>
                        <HorizontalChartUnified
                          data={{
                            kpi: line?.kpi?.[1],
                            ...props,
                            graphKeys: props?.graphKeys?.[1],
                            rankingKpiName: props?.rankingKpiName?.[1],
                            activeKeys: activeKeys[1],
                            combinedKeysIndex: 1,
                            maxValue,
                            activeTooltip,
                            setActiveTooltip: (data: any) =>
                              setActiveTooltip(data),
                            key: `energy_horzontal_chart_${lineIndex}_1`,
                            tooltipColors: props?.tooltipColors?.[1],
                            ...(lineIndex === lineArray.length - 1
                              ? { setRendered }
                              : {}),
                          }}
                        />
                      </div>
                    )}
                  </div>
                )
              )
            ) : props?.context === 'economics' ? (
              (data || []).map((item: any, index: number, itemArray: any) => (
                <div
                  key={`economics_horzontal_chart_${index}`}
                  className={styles.HorizontalChart__Wrapper__Line}
                >
                  <div
                    className={styles.HorizontalChart__Wrapper__Line__Label}
                    onClick={() => handleNavigation(item)}
                  >

                    <span>{item?.siteName}</span>

                      <span className="font-flexo-light">
                        &nbsp; {item?.pod}
                      </span>
                  </div>
                  <div className={styles.HorizontalChart__Wrapper__Line__Rank}>
                    <div
                      className={
                        styles.HorizontalChart__Wrapper__Line__Rank__Value__Name__Wrapper
                      }
                    >
                      <p
                        className={
                          styles.HorizontalChart__Wrapper__Line__Rank__Value
                        }
                      >
                        {getKpiValue(item)}
                      </p>
                      <p
                        className={
                          styles.HorizontalChart__Wrapper__Line__Rank__Name
                        }
                      >
                        {' '}
                        {t(props?.rankingKpiName)}
                      </p>
                    </div>
                    <HorizontalChartUnified
                      data={{
                        kpi: item?.kpi,
                        ...props,
                        activeKeys,
                        maxValue,
                        ...(index === itemArray.length - 1
                          ? { setRendered: setRendered }
                          : {}),
                      }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <></>
            )}
          </div>
        </>
      )}
    </div>
  );
}
